import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, resolveImg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import ImageUpload from "./ImageUpload";

const EMPTY = { title_ar: "", title_en: "", desc_ar: "", desc_en: "", image_url: "", old_price: 0, new_price: 0, start_date: "", expiry_date: "", status: "published" };
const STATUS_AR = { published: "منشور", paused: "متوقف" };

export default function OffersManage() {
  const [offers, setOffers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get("/admin/offers").then((r) => setOffers(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/admin/offers/${data.id}`, data);
      else await api.post("/admin/offers", data);
      toast.success("تم الحفظ"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("حذف العرض؟")) return; await api.delete(`/admin/offers/${id}`); toast.success("تم الحذف"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">إدارة العروض</h1>
        <Button onClick={() => setEditing({ ...EMPTY })} className="rounded-full" data-testid="add-offer-btn"><Plus className="h-4 w-4 me-1" /> عرض جديد</Button>
      </div>
      {loading ? <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mt-10" /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {offers.map((o) => (
            <div key={o.id} className="rounded-2xl bg-card cb-card-shadow overflow-hidden" data-testid={`admin-offer-${o.id}`}>
              <div className="relative aspect-video bg-muted"><img src={resolveImg(o.image_url)} alt="" className="h-full w-full object-cover" />
                <span className={`absolute top-2 start-2 px-2 py-0.5 rounded-full text-xs text-white ${o.status === "published" ? "bg-emerald-500" : "bg-slate-500"}`}>{STATUS_AR[o.status]}</span>
              </div>
              <div className="p-4">
                <h3 className="font-semibold">{o.title_ar}</h3>
                <div className="flex items-center gap-2 mt-1"><span className="text-primary font-bold">{o.new_price} ر.س</span><span className="text-muted-foreground line-through text-sm">{o.old_price}</span></div>
                <p className="text-xs text-muted-foreground mt-1">ينتهي: {o.expiry_date}</p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => setEditing(o)} className="rounded-full border-2 flex-1" data-testid={`edit-offer-${o.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(o.id)} className="text-destructive" data-testid={`delete-offer-${o.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <OfferDialog editing={editing} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}

function OfferDialog({ editing, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY);
  const [genBusy, setGenBusy] = useState(false);
  useEffect(() => { if (editing) setForm(editing); }, [editing]);
  if (!editing) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    if (!form.title_ar.trim()) { toast.error("أدخلي عنوان العرض أولاً"); return; }
    setGenBusy(true);
    try {
      const { data } = await api.post("/admin/ai/generate-description", { name: form.title_ar, category: "عرض", kind: "offer" });
      setForm((f) => ({ ...f, desc_ar: data.ar || f.desc_ar, desc_en: data.en || f.desc_en }));
      toast.success("تم توليد الوصف");
    } catch (e) { toast.error(apiErr(e)); } finally { setGenBusy(false); }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader><DialogTitle>{form.id ? "تعديل عرض" : "عرض جديد"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mb-1 block text-xs">العنوان (عربي)</Label><Input value={form.title_ar} onChange={(e) => set("title_ar", e.target.value)} className="rounded-xl h-11" data-testid="offer-title-ar" /></div>
            <div><Label className="mb-1 block text-xs">العنوان (إنجليزي)</Label><Input value={form.title_en} onChange={(e) => set("title_en", e.target.value)} className="rounded-xl h-11" dir="ltr" /></div>
          </div>
          <div className="flex justify-end">
            <Button type="button" size="sm" variant="outline" onClick={generate} disabled={genBusy} className="rounded-full border-2 text-primary" data-testid="offer-ai-generate">
              {genBusy ? <Loader2 className="h-4 w-4 animate-spin me-1" /> : <Sparkles className="h-4 w-4 me-1" />} اكتبي الوصف بالذكاء الاصطناعي
            </Button>
          </div>
          <div><Label className="mb-1 block text-xs">الوصف (عربي)</Label><Textarea value={form.desc_ar} onChange={(e) => set("desc_ar", e.target.value)} className="rounded-xl" rows={2} /></div>
          <div><Label className="mb-1 block text-xs">الوصف (إنجليزي)</Label><Textarea value={form.desc_en} onChange={(e) => set("desc_en", e.target.value)} className="rounded-xl" rows={2} dir="ltr" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mb-1 block text-xs">السعر السابق</Label><Input type="number" value={form.old_price} onChange={(e) => set("old_price", +e.target.value)} className="rounded-xl h-11" data-testid="offer-old-price" /></div>
            <div><Label className="mb-1 block text-xs">السعر بعد الخصم</Label><Input type="number" value={form.new_price} onChange={(e) => set("new_price", +e.target.value)} className="rounded-xl h-11" data-testid="offer-new-price" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mb-1 block text-xs">تاريخ البدء</Label><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="rounded-xl h-11" /></div>
            <div><Label className="mb-1 block text-xs">تاريخ الانتهاء</Label><Input type="date" value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} className="rounded-xl h-11" data-testid="offer-expiry" /></div>
          </div>
          <ImageUpload value={form.image_url} onChange={(v) => set("image_url", v)} label="صورة العرض" />
          <div>
            <Label className="mb-1 block text-xs">الحالة</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="rounded-xl h-11" data-testid="offer-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="published">منشور</SelectItem><SelectItem value="paused">متوقف</SelectItem></SelectContent>
            </Select>
          </div>
          <Button onClick={() => onSave(form)} className="rounded-full w-full h-11 font-semibold" data-testid="offer-save-btn">حفظ</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
